/**
 * SiteMetaSettings.tsx
 *
 * Admin UI for editing global site meta:
 * - OG image (social share preview image)
 * - Favicon (browser tab / toolbar icon)
 * - Site name, description, domain
 * - Keywords/tags
 * - Google Search Console verification
 * - Instagram post URLs for embedded feed
 *
 * All values are stored under the key "ma_site_meta" in the staging store
 * and synced to Supabase on Save. The prerender-site edge function reads
 * these values to inject correct meta tags for crawlers and social platforms.
 */
import { useState, useRef, useCallback } from "react";
import { useAdminStorage } from "@/hooks/useAdminStorage";
import { useDarkMode } from "@/context/DarkModeContext";
import { uploadFileToCloud } from "@/utils/cloudImageUpload";
import type { InstagramPost } from "@/components/feature/InstagramFeedSection";

export interface SiteMetaData {
  siteName: string;
  description: string;
  domain: string;
  ogImage: string;
  favicon: string;
  tags: string;
  googleVerification: string;
}

const DEFAULT_SITE_META: SiteMetaData = {
  siteName: "Makers Alley Co",
  description: "A permanent, algorithm-free home for independent makers — where your work is seen, celebrated, and sold year-round.",
  domain: "https://makersalley.co",
  ogImage: "",
  favicon: "/favicon.png",
  tags: "handmade artists, maker marketplace, year-round retail space, artist shelf rentals, sell handmade art, indie creators, artisan shop, Medford Oregon retail",
  googleVerification: "",
};

export const SITE_META_KEY = "ma_site_meta";

interface UploadState {
  uploading: boolean;
  error: string | null;
}

function extractShortcode(url: string): string | null {
  // Matches /p/SHORTCODE/, /reel/SHORTCODE/, /tv/SHORTCODE/
  // Also handles username prefix: instagram.com/username/p/SHORTCODE/
  const match = url.match(/instagram\.com\/(?:[^/]+\/)?(?:p|reel|tv)\/([^/?#]+)/);
  return match?.[1] ?? null;
}

export default function SiteMetaSettings() {
  const { isDark } = useDarkMode();
  const [meta, setMeta] = useAdminStorage<SiteMetaData>(SITE_META_KEY, DEFAULT_SITE_META);
  const [saved, setSaved] = useState(false);
  const [ogUpload, setOgUpload] = useState<UploadState>({ uploading: false, error: null });
  const [faviconUpload, setFaviconUpload] = useState<UploadState>({ uploading: false, error: null });
  const ogInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);

  // Instagram post management
  const [posts, setPosts] = useAdminStorage<InstagramPost[]>("ma_instagram_posts", []);
  const [newUrl, setNewUrl] = useState("");
  const [igError, setIgError] = useState<string | null>(null);

  const headingColor = isDark ? "text-white" : "text-gray-800";
  const subColor = isDark ? "text-gray-400" : "text-gray-500";
  const cardBg = isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200";
  const inputCls = `w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-200 ${
    isDark ? "border-gray-600 bg-gray-700 text-white placeholder-gray-500" : "border-gray-200 bg-white text-gray-800"
  }`;

  const update = <K extends keyof SiteMetaData>(key: K, val: SiteMetaData[K]) => {
    setMeta({ ...meta, [key]: val });
  };

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleOgUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setOgUpload({ uploading: true, error: null });
    try {
      const url = await uploadFileToCloud(file);
      update("ogImage", url);
      setOgUpload({ uploading: false, error: null });
    } catch (err) {
      setOgUpload({ uploading: false, error: err instanceof Error ? err.message : "Upload failed" });
    }
    if (ogInputRef.current) ogInputRef.current.value = "";
  };

  const handleFaviconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFaviconUpload({ uploading: true, error: null });
    try {
      const url = await uploadFileToCloud(file);
      update("favicon", url);
      setFaviconUpload({ uploading: false, error: null });
    } catch (err) {
      setFaviconUpload({ uploading: false, error: err instanceof Error ? err.message : "Upload failed" });
    }
    if (faviconInputRef.current) faviconInputRef.current.value = "";
  };

  const addInstagramPost = useCallback(() => {
    const trimmed = newUrl.trim();
    if (!trimmed) return;
    const shortcode = extractShortcode(trimmed);
    if (!shortcode) {
      setIgError("Invalid Instagram URL. Must be like https://www.instagram.com/p/ABC123/");
      return;
    }
    const next: InstagramPost = {
      id: `ig-${shortcode}-${Date.now()}`,
      url: trimmed,
      caption: "",
    };
    setPosts([...(posts || []), next]);
    setNewUrl("");
    setIgError(null);
  }, [newUrl, posts, setPosts]);

  const removeInstagramPost = useCallback((id: string) => {
    setPosts((posts || []).filter((p) => p.id !== id));
  }, [posts, setPosts]);

  const moveInstagramPost = useCallback((idx: number, dir: -1 | 1) => {
    const arr = [...(posts || [])];
    const target = idx + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    setPosts(arr);
  }, [posts, setPosts]);

  const currentDomain = (meta.domain || "https://makersalley.co").replace(/\/$/, "");
  const prerenderUrl = `${currentDomain}/functions/v1/prerender-site?path=/`;

  return (
    <div className={`rounded-2xl border p-5 space-y-6 ${cardBg}`}>
      <div>
        <h3 className={`font-bold text-sm mb-1 ${headingColor}`}>
          <i className="ri-global-line text-pink-400 mr-1.5"></i>
          Site Identity &amp; SEO Meta
        </h3>
        <p className={`text-xs ${subColor}`}>
          These settings control how your site appears in search results, social media shares, and browser tabs.
          Changes are saved to Supabase and read by crawlers via the prerender edge function.
        </p>
      </div>

      {/* Site Name */}
      <div>
        <label className={`text-xs font-semibold mb-1.5 block ${subColor}`}>Site Name</label>
        <input
          type="text"
          value={meta.siteName}
          onChange={(e) => update("siteName", e.target.value)}
          placeholder="Makers Alley Co"
          className={inputCls}
        />
        <p className={`text-xs mt-1 ${subColor}`}>Appears in browser tabs, search results, and social shares.</p>
      </div>

      {/* Description */}
      <div>
        <label className={`text-xs font-semibold mb-1.5 block ${subColor}`}>Default Site Description</label>
        <textarea
          value={meta.description}
          onChange={(e) => update("description", e.target.value)}
          rows={3}
          maxLength={160}
          placeholder="A short description of your site (max 160 characters)"
          className={`${inputCls} resize-none`}
        />
        <p className={`text-xs mt-1 ${subColor}`}>{meta.description.length}/160 characters. Used as fallback description for pages without specific descriptions.</p>
      </div>

      {/* Domain */}
      <div>
        <label className={`text-xs font-semibold mb-1.5 block ${subColor}`}>Site Domain</label>
        <input
          type="text"
          value={meta.domain}
          onChange={(e) => update("domain", e.target.value.replace(/\/$/, ""))}
          placeholder="https://makersalley.co"
          className={inputCls}
        />
        <p className={`text-xs mt-1 ${subColor}`}>Used for canonical URLs and absolute links in structured data. No trailing slash.</p>
      </div>

      {/* Tags / Keywords */}
      <div>
        <label className={`text-xs font-semibold mb-1.5 block ${subColor}`}>
          <i className="ri-price-tag-3-line mr-1"></i>
          Site Tags / Keywords
        </label>
        <textarea
          value={meta.tags}
          onChange={(e) => update("tags", e.target.value)}
          rows={2}
          placeholder="handmade, ceramics, jewelry, Medford Oregon, retail space..."
          className={`${inputCls} resize-none`}
        />
        <p className={`text-xs mt-1 ${subColor}`}>
          Comma-separated keywords used site-wide for SEO. Individual pages can have their own keywords too.
        </p>
      </div>

      {/* Google Verification */}
      <div>
        <label className={`text-xs font-semibold mb-1.5 block ${subColor}`}>
          <i className="ri-search-line mr-1"></i>
          Google Search Console Verification
        </label>
        <input
          type="text"
          value={meta.googleVerification}
          onChange={(e) => update("googleVerification", e.target.value)}
          placeholder="Paste your google-site-verification code here"
          className={inputCls}
        />
        <p className={`text-xs mt-1 ${subColor}`}>
          Get this from{" "}
          <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer nofollow" className="text-pink-500 hover:text-pink-700 underline">
            Google Search Console
          </a>{" "}
          &rarr; Add property &rarr; HTML tag method. Paste the content value here (not the full tag).
        </p>
      </div>

      {/* OG Image */}
      <div>
        <label className={`text-xs font-semibold mb-1.5 block ${subColor}`}>
          <i className="ri-image-line mr-1"></i>
          Social Share Image (OG Image)
        </label>
        <p className={`text-xs mb-3 ${subColor}`}>
          This image appears when someone shares your site on Facebook, Twitter, iMessage, Slack, etc.
          Recommended size: <strong className={headingColor}>1200 × 630px</strong> (landscape). PNG or JPG.
        </p>

        {meta.ogImage ? (
          <div className="mb-3">
            <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-50" style={{ aspectRatio: "1200/630", maxHeight: "200px" }}>
              <img
                src={meta.ogImage}
                alt="OG preview"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 flex items-end p-2">
                <span className="text-[10px] font-bold bg-black/60 text-white px-2 py-0.5 rounded-full">
                  1200 × 630 preview
                </span>
              </div>
            </div>
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => ogInputRef.current?.click()}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap transition-colors ${isDark ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
              >
                <i className="ri-upload-line mr-1"></i>Replace Image
              </button>
              <button
                onClick={() => update("ogImage", "")}
                className="text-xs font-semibold px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap transition-colors text-red-400 hover:text-red-600 hover:bg-red-50"
              >
                <i className="ri-close-line mr-1"></i>Remove
              </button>
            </div>
          </div>
        ) : (
          <div
            onClick={() => ogInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
              isDark ? "border-gray-600 hover:border-pink-400" : "border-pink-200 hover:border-pink-400"
            }`}
          >
            {ogUpload.uploading ? (
              <div className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin text-pink-400 text-xl"></i>
                <span className={`text-sm font-semibold ${subColor}`}>Uploading...</span>
              </div>
            ) : (
              <>
                <i className={`ri-image-add-line text-3xl mb-2 block ${isDark ? "text-gray-500" : "text-pink-300"}`}></i>
                <p className={`text-sm font-semibold ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Click to upload OG image
                </p>
                <p className={`text-xs mt-1 ${isDark ? "text-gray-600" : "text-gray-400"}`}>
                  PNG or JPG, 1200 × 630px recommended
                </p>
              </>
            )}
          </div>
        )}

        {ogUpload.error && (
          <p className="text-red-500 text-xs mt-1">{ogUpload.error}</p>
        )}

        <input
          ref={ogInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={handleOgUpload}
        />
      </div>

      {/* Favicon */}
      <div>
        <label className={`text-xs font-semibold mb-1.5 block ${subColor}`}>
          <i className="ri-bookmark-line mr-1"></i>
          Favicon / Browser Tab Icon
        </label>
        <p className={`text-xs mb-3 ${subColor}`}>
          The small icon that appears in browser tabs, bookmarks, and the address bar.
          Recommended: <strong className={headingColor}>32 × 32px or 64 × 64px</strong> PNG with transparent background.
        </p>

        <div className="flex items-center gap-4 mb-3">
          <div className={`w-12 h-12 flex items-center justify-center rounded-xl border flex-shrink-0 ${isDark ? "bg-gray-700 border-gray-600" : "bg-gray-50 border-gray-200"}`}>
            {meta.favicon && meta.favicon !== "/favicon.png" ? (
              <img src={meta.favicon} alt="Favicon preview" className="w-8 h-8 object-contain" />
            ) : (
              <i className={`ri-bookmark-line text-2xl ${subColor}`}></i>
            )}
          </div>
          <div className="flex-1">
            <p className={`text-xs font-semibold mb-1 ${headingColor}`}>
              {meta.favicon && meta.favicon !== "/favicon.png" ? "Custom favicon set" : "Using default favicon"}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => faviconInputRef.current?.click()}
                disabled={faviconUpload.uploading}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50 ${isDark ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
              >
                {faviconUpload.uploading ? (
                  <><i className="ri-loader-4-line animate-spin mr-1"></i>Uploading...</>
                ) : (
                  <><i className="ri-upload-line mr-1"></i>Upload Icon</>
                )}
              </button>
              {meta.favicon && meta.favicon !== "/favicon.png" && (
                <button
                  onClick={() => update("favicon", "/favicon.png")}
                  className="text-xs font-semibold px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap transition-colors text-red-400 hover:text-red-600 hover:bg-red-50"
                >
                  Reset to Default
                </button>
              )}
            </div>
          </div>
        </div>

        {faviconUpload.error && (
          <p className="text-red-500 text-xs mb-2">{faviconUpload.error}</p>
        )}

        <input
          ref={faviconInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/x-icon"
          className="hidden"
          onChange={handleFaviconUpload}
        />
      </div>

      {/* Instagram Posts */}
      <div className="border-t border-dashed pt-5">
        <h3 className={`font-bold text-sm mb-1 ${headingColor}`}>
          <i className="ri-instagram-line text-pink-400 mr-1.5"></i>
          Embedded Instagram Posts
        </h3>
        <p className={`text-xs ${subColor} mb-3`}>
          Add Instagram post URLs here to embed them in the "Instagram Feed" section on your homepage.
          Paste the full post URL (e.g. https://www.instagram.com/p/ABC123/).
        </p>

        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={newUrl}
            onChange={(e) => { setNewUrl(e.target.value); setIgError(null); }}
            onKeyDown={(e) => { if (e.key === "Enter") addInstagramPost(); }}
            placeholder="https://www.instagram.com/p/..."
            className={`${inputCls} flex-1`}
          />
          <button
            onClick={addInstagramPost}
            className="bg-pink-500 hover:bg-pink-600 text-white font-bold text-xs px-4 py-2 rounded-full cursor-pointer whitespace-nowrap transition-colors flex-shrink-0"
          >
            <i className="ri-add-line mr-1"></i>Add Post
          </button>
        </div>

        {igError && <p className="text-red-500 text-xs mb-2">{igError}</p>}

        {(posts || []).length === 0 ? (
          <p className={`text-xs italic ${subColor}`}>No posts added yet.</p>
        ) : (
          <div className="space-y-2">
            {(posts || []).map((post, idx) => {
              const sc = extractShortcode(post.url);
              return (
                <div
                  key={post.id}
                  className={`flex items-center gap-3 p-2.5 rounded-lg border text-xs ${isDark ? "bg-gray-700 border-gray-600" : "bg-gray-50 border-gray-200"}`}
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-md bg-pink-100 flex-shrink-0">
                    <i className="ri-instagram-line text-pink-500"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold truncate ${headingColor}`}>
                      {sc ? `Post ${sc}` : "Instagram Post"}
                    </p>
                    <p className={`truncate ${subColor}`}>{post.url}</p>
                  </div>
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <button
                      onClick={() => moveInstagramPost(idx, -1)}
                      disabled={idx === 0}
                      className={`w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-200 text-gray-500 disabled:opacity-30 cursor-pointer transition-colors ${isDark ? "hover:bg-gray-600 text-gray-400" : ""}`}
                      title="Move up"
                    >
                      <i className="ri-arrow-up-line"></i>
                    </button>
                    <button
                      onClick={() => moveInstagramPost(idx, 1)}
                      disabled={idx === (posts || []).length - 1}
                      className={`w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-200 text-gray-500 disabled:opacity-30 cursor-pointer transition-colors ${isDark ? "hover:bg-gray-600 text-gray-400" : ""}`}
                      title="Move down"
                    >
                      <i className="ri-arrow-down-line"></i>
                    </button>
                    <button
                      onClick={() => removeInstagramPost(post.id)}
                      className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors flex-shrink-0 cursor-pointer"
                      title="Remove post"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Social Media Testing Tools */}
      <div className={`rounded-xl p-4 ${isDark ? "bg-gray-700/50" : "bg-pink-50"}`}>
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-pink-100 flex-shrink-0">
            <i className="ri-share-line text-pink-600 text-sm"></i>
          </div>
          <div className="w-full">
            <p className={`text-xs font-bold mb-2 ${headingColor}`}>Test Your Social Share Preview</p>
            <p className={`text-xs leading-relaxed ${subColor} mb-3`}>
              After saving, use these tools to see exactly how your site looks when shared on social media.
              If the preview looks wrong, the prerender function may need a moment to update.
            </p>
            <div className="flex flex-wrap gap-2">
              <a
                href="https://developers.facebook.com/tools/debug/?q=https://makersalley.co/"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-facebook-line"></i> Facebook Debugger
              </a>
              <a
                href="https://cards-dev.twitter.com/validator"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-sky-100 text-sky-700 hover:bg-sky-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-twitter-x-line"></i> Twitter Card Validator
              </a>
              <a
                href={prerenderUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-green-100 text-green-700 hover:bg-green-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-code-line"></i> View Prerender HTML
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Google Search Console / Indexing info */}
      <div className={`rounded-xl p-4 ${isDark ? "bg-gray-700/50" : "bg-green-50"}`}>
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-green-100 flex-shrink-0">
            <i className="ri-robot-line text-green-600 text-sm"></i>
          </div>
          <div className="w-full">
            <p className={`text-xs font-bold mb-1 ${headingColor}`}>Google Search &amp; Crawler Info</p>
            <p className={`text-xs leading-relaxed ${subColor}`}>
              The <strong className={headingColor}>prerender-site</strong> edge function reads your live content
              from Supabase and returns HTML with correct meta tags for crawlers.
              When Google, Facebook, or Twitter visits your site, they see your actual edited content.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <a
                href="https://search.google.com/search-console"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-google-line"></i> Open Search Console
              </a>
              <a
                href="https://search.google.com/test/rich-results"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-bard-line"></i> Rich Results Test
              </a>
            </div>
            <p className={`text-xs mt-2 ${subColor}`}>
              <i className="ri-information-line mr-1"></i>
              Prerender URL:{" "}
              <code className={`text-xs px-1 rounded ${isDark ? "bg-gray-600 text-pink-300" : "bg-white text-pink-600"}`}>
                {prerenderUrl}
              </code>
            </p>
          </div>
        </div>
      </div>

      <button
        onClick={handleSave}
        className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 rounded-full cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2"
      >
        {saved ? (
          <><i className="ri-check-line"></i>Staged! Click Save in the bar below to sync to Supabase</>
        ) : (
          <><i className="ri-save-line"></i>Apply Changes</>
        )}
      </button>
    </div>
  );
}